/**
 * Shared helpers for the diagram editor used by worksheet editing surfaces
 * (EditWorksheetModal, OneQuestionAtATime, ...). Keeps diagram parsing,
 * TikZ-to-SVG conversion and preview document building in one place.
 */

import axios from 'axios';
import { renderTikzToSvg } from './tikzRender';

export const INITIAL_DIAGRAM_EDITOR_STATE = {
  isOpen: false,
  questionIndex: null,
  questionNumber: null,
  tikzCode: '',
  originalTikzCode: '',
  svgCode: '',
  originalSvgCode: '',
  editorMode: 'tikz',
  excalidrawOpen: false,
  excalidrawElements: null,
  excalidrawFiles: null,
  // Emoji (picture-chart) diagrams: editable rows of { label, items } plus
  // the original diagram object so extra fields (description, font size,
  // libraries, ...) survive a save.
  emojiRows: [],
  originalEmojiRows: [],
  emojiBaseDiagram: null,
  emojiIsFlatList: false,
};

// AI sometimes emits non-canonical SVG type names ("inline_svg",
// "inline-svg", "inlinesvg") instead of "svg". Normalize them so
// downstream consumers route the code into the SVG editor / renderer
// rather than the TikZ branch.
const SVG_TYPE_ALIASES = new Set([
  'svg',
  'inline_svg',
  'inline-svg',
  'inlinesvg',
  'inline svg',
]);

const looksLikeSvgMarkup = (code) =>
  typeof code === 'string'
  && /<\?xml|<svg[\s>]|xmlns="http:\/\/www\.w3\.org\/2000\/svg"/i.test(code);

export const normalizeDiagramType = (rawType) => {
  const trimmed = (rawType || '').toLowerCase().trim();
  if (SVG_TYPE_ALIASES.has(trimmed)) return 'svg';
  return trimmed;
};

// Some AI outputs over-escape quotes inside SVG markup (\" instead of "),
// which breaks HTML parsing and produces a blank render. Strip the spurious
// escapes so the browser can parse the markup.
export const cleanSvgMarkup = (svgMarkup) => {
  if (!svgMarkup || typeof svgMarkup !== 'string') return svgMarkup;
  return svgMarkup.includes('\\"') ? svgMarkup.replace(/\\"/g, '"') : svgMarkup;
};

export const getDiagramType = (question) => {
  if (!question?.diagram) return '';
  if (typeof question.diagram === 'object') {
    const normalized = normalizeDiagramType(question.diagram.type);
    if (normalized) return normalized;
    // No declared type — peek at the code so raw <svg> markup doesn't get
    // silently routed to the TikZ branch.
    return looksLikeSvgMarkup(question.diagram.code) ? 'svg' : 'tikz';
  }
  if (typeof question.diagram === 'string' && question.diagram.trim()) {
    return looksLikeSvgMarkup(question.diagram) ? 'svg' : 'tikz';
  }
  return '';
};

export const isEmojiDiagram = (question) =>
  typeof question?.diagram === 'object'
  && question.diagram !== null
  && normalizeDiagramType(question.diagram.type) === 'emoji';

// Emoji diagram labels are either a 2D grid (rows of cells) or a flat list
// (a single row). Always return rows of string cells.
export const getEmojiDiagramGrid = (diagram) => {
  const labels = diagram?.labels;
  if (!Array.isArray(labels) || labels.length === 0) return [];
  if (labels.some((row) => Array.isArray(row))) {
    return labels
      .map((row) => (Array.isArray(row) ? row : [row]))
      .map((row) => row.filter((cell) => cell !== null && cell !== undefined).map(String));
  }
  return [labels.filter((cell) => cell !== null && cell !== undefined).map(String)];
};

export const hasEmojiDiagram = (question) =>
  isEmojiDiagram(question)
  && getEmojiDiagramGrid(question.diagram).some((row) => row.some((cell) => cell.trim()));

// A leading cell containing letters/digits ("Cars:") is the row label; the
// rest are the picture items (emojis).
const isLabelCell = (cell) => /[\p{L}\p{N}]/u.test(cell || '');

export const emojiGridToRows = (grid) =>
  (grid || []).map((row) => {
    const cells = row.map((cell) => String(cell));
    const hasLabel = cells.length > 0 && isLabelCell(cells[0]);
    return {
      label: hasLabel ? cells[0] : '',
      items: (hasLabel ? cells.slice(1) : cells).join(' '),
    };
  });

export const emojiRowsToGrid = (rows) =>
  (rows || [])
    .map((row) => {
      const label = (row.label || '').trim();
      const items = (row.items || '').split(/\s+/).filter(Boolean);
      return label ? [label, ...items] : items;
    })
    .filter((row) => row.length > 0);

export const getDiagramCode = (question) => {
  if (!question?.diagram) return '';
  if (typeof question.diagram === 'object') return question.diagram.code || '';
  if (typeof question.diagram === 'string') return question.diagram;
  return '';
};

export const hasDiagram = (question) => {
  if (hasEmojiDiagram(question)) return true;
  const diagramCode = getDiagramCode(question);
  return typeof diagramCode === 'string' && diagramCode.trim().length > 0;
};

export const isTikzLikeDiagram = (diagramType = '') =>
  ['tikz', 'latex', 'katex', 'chemfig'].includes((diagramType || '').toLowerCase());

export const normalizeSvgForDisplay = (svgMarkup) => {
  if (!svgMarkup || typeof svgMarkup !== 'string') return '';

  const cleaned = cleanSvgMarkup(svgMarkup);

  try {
    const parser = new DOMParser();
    const parsed = parser.parseFromString(cleaned, 'image/svg+xml');
    const svg = parsed.querySelector('svg');
    if (!svg) return cleaned;

    const existingStyle = svg.getAttribute('style') || '';
    const normalizedStyle = [
      existingStyle.trim(),
      'display:block',
      'margin:0 auto',
      'max-width:100%',
      'height:auto',
      'position:static'
    ].filter(Boolean).join('; ');

    svg.setAttribute('style', normalizedStyle);
    return svg.outerHTML;
  } catch (normalizeError) {
    console.error('Unable to normalize SVG markup:', normalizeError);
    return cleaned;
  }
};

// Strips the parts of SVG markup that can execute script when the result is
// later injected via dangerouslySetInnerHTML: <script>/<foreignObject>
// elements (which can smuggle arbitrary HTML), any on* event handler
// attribute, and javascript: URIs on href/xlink:href. Browsers already
// refuse to run a <script> tag inserted via innerHTML, but SVG event
// handler attributes (onload, onerror, ...) still fire once attached to a
// live DOM, so those need removing explicitly. Fails closed: unparsable
// input returns an empty string rather than passing it through.
export const sanitizeSvgMarkup = (svgMarkup) => {
  if (!svgMarkup || typeof svgMarkup !== 'string') return '';
  try {
    const parser = new DOMParser();
    const parsed = parser.parseFromString(svgMarkup, 'image/svg+xml');
    if (parsed.querySelector('parsererror')) return '';

    const svg = parsed.querySelector('svg');
    if (!svg) return '';

    svg.querySelectorAll('script, foreignObject').forEach((node) => node.remove());

    const walker = parsed.createTreeWalker(svg, NodeFilter.SHOW_ELEMENT);
    let node = walker.currentNode;
    while (node) {
      [...node.attributes].forEach((attr) => {
        const name = attr.name.toLowerCase();
        const value = attr.value.trim().toLowerCase();
        if (name.startsWith('on') || ((name === 'href' || name === 'xlink:href') && value.startsWith('javascript:'))) {
          node.removeAttribute(attr.name);
        }
      });
      node = walker.nextNode();
    }

    return svg.outerHTML;
  } catch (sanitizeError) {
    console.error('Unable to sanitize SVG markup:', sanitizeError);
    return '';
  }
};

// Server-side conversion via the renderer sidecar. Returns raw SVG markup or
// null on failure so callers can fall back to the in-browser converter.
export const requestTikzToSvg = async (tikzCode, libraries) => {
  if (!tikzCode || !tikzCode.trim()) return null;
  try {
    const { data } = await axios.post('/diagrams/tikz-to-svg', {
      code: tikzCode,
      ...(libraries ? { libraries } : {}),
    });
    return data?.svg || null;
  } catch (_error) {
    return null;
  }
};

// Preferred path for the diagram editor: try the server sidecar first
// (fast, ~500ms when healthy) then fall back to the in-browser TikZJax
// renderer that powers DiagramTile. The legacy `convertTikzToSvg` path
// below relies on `window.tikzjax.process()`, which TikZJax v1 does not
// reliably expose for ad-hoc calls — without this fallback "Regenerate
// SVG Preview" silently fails whenever the sidecar is down.
export const renderTikzToSvgWithFallback = async (tikzCode, options = {}) => {
  if (!tikzCode || !String(tikzCode).trim()) return null;
  const serverSvg = await requestTikzToSvg(tikzCode, options.libraries);
  if (serverSvg) return serverSvg;
  try {
    const svg = await renderTikzToSvg(tikzCode, {
      timeoutMs: options.timeoutMs ?? 25000,
      signal: options.signal,
    });
    return svg || null;
  } catch (_renderError) {
    return null;
  }
};

export const convertTikzToSvg = async (tikzCode) => {
  if (!tikzCode || !document?.body) return null;

  return new Promise((resolve) => {
    const conversionFrame = document.createElement('iframe');
    conversionFrame.setAttribute('sandbox', 'allow-scripts');
    conversionFrame.style.position = 'fixed';
    conversionFrame.style.left = '-9999px';
    conversionFrame.style.width = '0';
    conversionFrame.style.height = '0';
    conversionFrame.style.border = '0';
    const frameWindowRef = conversionFrame.contentWindow;

    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      if (conversionFrame.parentNode) {
        conversionFrame.parentNode.removeChild(conversionFrame);
      }
    };

    const timeoutId = setTimeout(() => {
      cleanup();
      resolve(null);
    }, 10000);

    const handleMessage = (event) => {
      if (frameWindowRef && event.source !== frameWindowRef) return;
      if (!event.data || event.data.type !== 'tikz-svg-result') return;
      clearTimeout(timeoutId);
      cleanup();
      resolve(event.data.svg || null);
    };

    const escapedTikz = tikzCode
      .replace(/\\/g, '\\\\')
      .replace(/`/g, '\\`')
      .replace(/\$\{/g, '\\${');

    window.addEventListener('message', handleMessage);
    document.body.appendChild(conversionFrame);

    const srcDoc = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <script src="https://tikzjax.com/v1/tikzjax.js"></script>
  </head>
  <body>
    <div id="holder">
      <script type="text/tikz">
${escapedTikz}
      </script>
    </div>
    <script>
      (function() {
        const postResult = (svgMarkup) => {
          window.parent.postMessage({ type: 'tikz-svg-result', svg: svgMarkup }, '*');
        };

        try {
          const maxWaitMs = 7000;
          const startedAt = Date.now();

          const renderPoller = window.setInterval(() => {
            const holder = document.getElementById('holder');
            if (!holder) return;

            const svg = holder.querySelector('svg');
            if (svg && svg.outerHTML) {
              window.clearInterval(renderPoller);
              postResult(svg.outerHTML);
              return;
            }

            if (Date.now() - startedAt > maxWaitMs) {
              window.clearInterval(renderPoller);
              postResult(null);
            }
          }, 75);
        } catch (error) {
          postResult(null);
        }
      })();
    </script>
  </body>
</html>`;

    conversionFrame.srcdoc = srcDoc;
  });
};

export const buildSvgPreviewDoc = (svgCode) => {
  if (!svgCode) return '';

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      html, body { margin: 0; padding: 0; background: #f9fafb; }
      body { display: flex; justify-content: center; align-items: flex-start; min-height: 100vh; padding: 12px; box-sizing: border-box; overflow: auto; }
      svg { max-width: 100%; height: auto; display: block; margin: 0 auto; }
    </style>
  </head>
  <body>
    ${svgCode}
  </body>
</html>`;
};

export const buildTikzPreviewDoc = (tikzCode) => {
  if (!tikzCode) return '';
  const encodedTikz = encodeURIComponent(tikzCode);

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <link rel="stylesheet" type="text/css" href="https://tikzjax.com/v1/fonts.css" />
    <style>
      html, body { margin: 0; padding: 0; background: #f9fafb; }
      body { display: flex; justify-content: center; align-items: flex-start; min-height: 100vh; padding: 12px; box-sizing: border-box; overflow: auto; }
      svg { max-width: 100%; height: auto; display: block; margin: 0 auto; }
    </style>
  </head>
  <body>
    <div id="tikz-root"></div>
    <script>
      window.__TIKZ_CODE__ = decodeURIComponent(${JSON.stringify(encodedTikz)});
    </script>
    <script src="https://tikzjax.com/v1/tikzjax.js"></script>
    <script>
      (function () {
        const root = document.getElementById('tikz-root');
        if (!root) return;

        const source = document.createElement('script');
        source.type = 'text/tikz';
        source.textContent = window.__TIKZ_CODE__ || '';
        root.appendChild(source);

        let attempts = 0;
        const maxAttempts = 100;

        const render = () => {
          if (!window.tikzjax || typeof window.tikzjax.process !== 'function') return false;
          try {
            window.tikzjax.process(source);
          } catch (error) {
            return attempts >= maxAttempts;
          }
          return true;
        };

        const interval = window.setInterval(() => {
          attempts += 1;
          if (render() || attempts >= maxAttempts) {
            window.clearInterval(interval);
          }
        }, 50);
      })();
    </script>
  </body>
</html>`;
};
